import { Action } from '@reduxjs/toolkit';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { postFormData } from '@eika-infrastruktur/request';
import { Attachment, AttachmentStatus, FileUploader } from '@eika/file-uploader';

import { useBankregnr } from '../../config';
import { logger } from '../../logg/logger';
import { getRequestOptions } from '../../meldskade/reducer/skademelding-actions';
import { MAX_FILE_SIZE_BYTES } from '../../sporsmalsflyt/filopplasting/file-validation/file-validation';
import { allowedFiles, STORE_FILTYPER } from '../../sporsmalsflyt/filopplasting/file-validation/filtyper-blacklist';
import {
    UploadedVedleggResult,
    VedleggIdInternal,
    VedleggStatus,
    VedleggWithFile,
} from '../../sporsmalsflyt/filopplasting/filopplasting-types';
import { buildVedleggFormData } from '../../sporsmalsflyt/reducer/sporsmalsflyt-actions';
import { hentVedleggUrl } from '../../urler';
import { addUploadedAttachments, deleteUploadedAttachment } from '../reducer/ettersending-actions';
import {
    fromAttachmentToInternalAttachment,
    fromAttachmentToVedleggWithFile,
    InternalAttachment,
    toInternalAttachments,
} from './attachment-types';
import { partitionAttachments } from './ettersending-utils';

enum UploadingErrorText {
    SingleFile = 'Dobbeltsjekk at filen er gyldig. Hvis filen gjentatte ganger feiler under opplastning, så kan du dessverre ikke laste opp denne filen.',
    MultipleFiles = 'Dobbeltsjekk at filene er gyldige. Hvis filene gjentatte ganger feiler under opplastning, så kan du dessverre ikke laste opp disse filene.',
}

interface FileUploaderEttersendingProps {
    attachmentsFromState: InternalAttachment[];
    setIsLoading: (value: boolean) => void;
}

const FileUploaderEttersending: FC<FileUploaderEttersendingProps> = ({ attachmentsFromState, setIsLoading }) => {
    const dispatch: Dispatch<Action> = useDispatch();
    const bankregnr = useBankregnr();
    const [error, setError] = useState<UploadingErrorText | undefined>(undefined);

    const deleteAttachment = (attachment: Attachment) => {
        dispatch(deleteUploadedAttachment([fromAttachmentToInternalAttachment(attachment)]));
    };

    const upload = async (chosenAttachments: Attachment[]): Promise<Attachment[]> => {
        setIsLoading(true);
        setError(undefined);
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const existingIds: VedleggIdInternal[] = attachmentsFromState
                    .filter((f) => f.internalId === undefined)
                    .map((attachment: InternalAttachment) => {
                        return {
                            id: attachment.internalId,
                            fileName: attachment.fileName,
                        };
                    });

                postFormData(
                    hentVedleggUrl(),
                    buildVedleggFormData(fromAttachmentToVedleggWithFile(chosenAttachments), {
                        vedleggIds: existingIds.map((i) => {
                            return {
                                id: i.id,
                                fileName: i.fileName,
                            };
                        }),
                    }),
                    getRequestOptions(bankregnr),
                )
                    .then((vedleggResponse: UploadedVedleggResult) => {
                        const successfullyUploadedVedlegg: VedleggWithFile[] = vedleggResponse.vedlegg.filter(
                            (v) => v.status === VedleggStatus.Ok,
                        );

                        const [successfullyUploadedChosenAttachments, failedUploadedChosenAttachments]: Attachment[][] =
                            partitionAttachments(chosenAttachments, (attachment: Attachment) =>
                                successfullyUploadedVedlegg
                                    .map((vedlegg: VedleggWithFile) => vedlegg.uploadId)
                                    .includes(attachment.id),
                            );

                        dispatch(
                            addUploadedAttachments(
                                toInternalAttachments(
                                    successfullyUploadedVedlegg,
                                    successfullyUploadedChosenAttachments,
                                ),
                            ),
                        );
                        resolve(
                            chosenAttachments.map((attachment) => ({
                                ...attachment,
                                status: successfullyUploadedChosenAttachments.includes(attachment)
                                    ? AttachmentStatus.Ok
                                    : AttachmentStatus.Error,
                            })),
                        );
                        if (failedUploadedChosenAttachments.length === 1) setError(UploadingErrorText.SingleFile);
                        if (failedUploadedChosenAttachments.length > 1) setError(UploadingErrorText.MultipleFiles);
                    })
                    .catch((error: Error) => {
                        const log = logger(bankregnr);
                        log.warn(
                            `Failed to upload vedlegg in ettersending. Vedlegg-liste: ${chosenAttachments.map(
                                (attachment, index) =>
                                    `${index}: id - ${attachment.id}, size - ${attachment.size}, filename - ${attachment.fileName}. `,
                            )}`,
                            undefined,
                            undefined,
                            error,
                        );
                        reject(
                            chosenAttachments.map((attachment) => ({
                                ...attachment,
                                status: AttachmentStatus.Error,
                            })),
                        );
                    })
                    .finally(() => setIsLoading(false));
            }, 1000);
        });
    };

    return (
        <div className="ettersending__file-uploader">
            <p className="typography-subtitle">Last opp dokumenter</p>
            <FileUploader
                uploadAttachment={upload}
                removeAttachment={deleteAttachment}
                options={{
                    allowedFileTypes: allowedFiles,
                    largeFormatFileTypes: STORE_FILTYPER,
                    attachmentListHeading: undefined,
                    fileErrorText: error,
                    maxFileByteSize: MAX_FILE_SIZE_BYTES,
                }}
            />
        </div>
    );
};

export default FileUploaderEttersending;
